"use client";

import { useEffect, type ComponentProps, type MouseEvent } from "react";
import { Link, useRouter } from "@/navigation";
import { AD_REF_URL_PARAM, readAdRefFromLocation } from "@/lib/ad-ref";

type LoginLinkProps = Omit<ComponentProps<typeof Link>, "href">;

function pageAdRef(): string | undefined {
  return readAdRefFromLocation(typeof window === "undefined" ? null : window.location);
}

/**
 * A plain link to /login. On a page whose URL names an ad, a plain click
 * goes to /login?gg_adref=… instead, so the ad reaches the trial and the
 * sign-up in the URL only, never on the device (src/lib/ad-ref.ts; the login
 * page folds it into `next`). The rendered href stays /login, so server
 * renders and static shells are unchanged; new-tab clicks keep it.
 */
export default function LoginLink({ onClick, ...props }: LoginLinkProps): React.JSX.Element {
  const router = useRouter();

  // The link prefetches /login; an ad visitor goes to /login?gg_adref=…,
  // which is another cache entry, so prefetch that one too (after mount only).
  useEffect(() => {
    if (props.prefetch === false) return;
    try {
      const adRef = pageAdRef();
      if (adRef) router.prefetch({ pathname: "/login", query: { [AD_REF_URL_PARAM]: adRef } });
    } catch {
      // A prefetch is only a speed-up.
    }
  }, [props.prefetch, router]);

  function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const adRef = pageAdRef();
    if (!adRef) return;
    event.preventDefault();
    try {
      router.push({ pathname: "/login", query: { [AD_REF_URL_PARAM]: adRef } });
    } catch {
      // Never a dead link: the plain login page, without the ad reference.
      window.location.assign(event.currentTarget.href);
    }
  }

  return <Link {...props} href="/login" onClick={handleClick} />;
}
